import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Send, BookOpen, ArrowRight } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Stat } from "@/components/Layout";
import { MailTable } from "@/components/MailTable";
import { api, gbp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [sent, setSent] = useState([]);
  const [received, setReceived] = useState([]);
  const [problems, setProblems] = useState(null);
  const problemCount = sent.filter((m) => m.delivery_problem).length;
  const toggleProblems = () => {
    if (problems) { setProblems(null); return; }
    api.get("/mail/sent", { params: { problems: true } }).then((r) => setProblems(r.data));
  };

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data));
    api.get("/mail/sent").then((r) => setSent(r.data));
    api.get("/mail/received").then((r) => setReceived(r.data));
  }, []);

  const s = summary?.sent_by_status || {};
  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow="Overview" title={`Hello, ${user?.name?.split(" ")[0] || ""}`}>
        <Link to="/app/send" data-testid="dashboard-send-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors"><Send size={15} /> Send a letter</Link>
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <Stat testId="stat-awaiting-claim" label="Awaiting claim" value={s.pending_claim || 0} />
        <Stat testId="stat-claimed" label="Claimed" value={s.claimed || 0} />
        <Stat testId="stat-returned" label="Returned" value={s.returned || 0} />
        <Stat testId="stat-received" label="Received" value={summary?.received || 0} />
        <Stat testId="stat-spent" label="Spent" value={gbp(summary?.spent_gbp)} accent />
      </div>
      <Link to="/app/guide" data-testid="dashboard-guide-card" className="group mb-10 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/30 p-5 transition-colors hover:border-emerald-500">
        <span className="flex items-center gap-4">
          <span className="h-11 w-11 rounded-xl bg-emerald-600 text-white inline-flex items-center justify-center shrink-0"><BookOpen size={18} /></span>
          <span><span className="block font-display font-semibold">How to send with PostalCloud</span>
            <span className="block text-sm text-slate-600 dark:text-slate-400">Step-by-step guide to single letters and bulk CSV sending</span></span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-300">Open guide <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></span>
      </Link>
      <Tabs defaultValue="sent">
        <TabsList>
          <TabsTrigger value="sent" data-testid="dashboard-tab-sent">Sent ({sent.length})</TabsTrigger>
          <TabsTrigger value="received" data-testid="dashboard-tab-received">Received ({received.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="sent" className="mt-5 space-y-3">
          {(problemCount > 0 || problems) && (
            <button data-testid="sent-delivery-problems-filter" onClick={toggleProblems} aria-pressed={!!problems}
              className={`inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs font-semibold border transition-colors ${problems ? "bg-red-600 border-red-600 text-white" : "border-red-300 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40"}`}>
              <AlertTriangle size={13} /> {problems ? "Showing delivery problems · show all" : `Delivery problems (${problemCount})`}</button>
          )}
          <MailTable rows={problems || sent} mode="sent" empty={problems ? "No delivery problems right now." : "You haven't sent any letters yet."} />
        </TabsContent>
        <TabsContent value="received" className="mt-5"><MailTable rows={received} mode="received" empty="No claimed letters yet." /></TabsContent>
      </Tabs>
    </div>
  );
}
